import type { OcpiLocation } from "../types/ocpi";
import {
  aggregateStatus,
  formatKw,
  locationHasAc,
  locationHasDc,
  locationMaxKw,
  locationTier,
  totalConnectors,
} from "../utils/format";
import { useI18n } from "../i18n/LanguageContext";
import { StatusPill } from "./StatusPill";
import { BRAND } from "../config/brand";

const TIER_COLOR = {
  AC: BRAND.ac,
  DC_FAST: BRAND.dcFast,
  DC_ULTRA: BRAND.dcUltra,
} as const;

function formatDistance(km: number, lang: "tr" | "en"): string {
  if (km < 1) return lang === "tr" ? `${Math.round(km * 1000)} m` : `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

export function LocationCard({
  location,
  distanceKm,
  active,
  onClick,
}: {
  location: OcpiLocation;
  distanceKm: number | null;
  active?: boolean;
  onClick: () => void;
}) {
  const { t, lang } = useI18n();
  const kw = locationMaxKw(location);
  const tier = locationTier(location);
  const status = aggregateStatus(location);
  const hasAc = locationHasAc(location);
  const hasDc = locationHasDc(location);
  return (
    <button
      onClick={onClick}
      className={
        "group relative w-full text-left rounded-xl border p-3 transition " +
        (active
          ? "border-brand-500 bg-brand-50/50 shadow-soft"
          : "border-ink-100 bg-white hover:border-ink-200 hover:shadow-soft")
      }
    >
      <div className="flex items-start gap-3">
        <div
          className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg text-white shadow-soft"
          style={{ background: TIER_COLOR[tier] }}
          aria-hidden
        >
          <BoltIcon />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-[13.5px] font-semibold text-ink-900 leading-snug truncate">
              {location.name || location.id}
            </h3>
            <span className="text-[12px] font-bold tabular-nums text-ink-900 shrink-0">
              {formatKw(kw)}
            </span>
          </div>
          <p className="mt-0.5 text-[11.5px] text-ink-500 truncate">
            {location.city} · {location.address}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <StatusPill status={status} size="xs" />
            {hasAc && <span className="chip bg-brand-50 text-brand-700">{t("ac")}</span>}
            {hasDc && (
              <span className="chip bg-sky-50 text-sky-700">
                {t("dc")}
              </span>
            )}
            <span className="chip bg-ink-50 text-ink-600">
              {totalConnectors(location)} {t("sockets")}
            </span>
            {distanceKm != null && (
              <span className="chip ml-auto bg-ink-900 text-white tabular-nums">
                {formatDistance(distanceKm, lang)}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

function BoltIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
    </svg>
  );
}
